<script>
  // eslint-disable-next-line no-unused-vars
  import * as Types from "../NewsLetter/newsLetterTypes";

  // @ts-check
  import { createEventDispatcher } from "svelte";

  import Link from "../../atoms/Link.svelte";
  import FormField from "../../molecules/FormFields/FormField.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = "";

  /**
   * @type { "split" | "stacked" }
   */
  export let layout = "split";

  /**
   * Optional prop for handling form submission natively
   *
   * @type {Types.FormProps}
   * */
  export let formProps = {};

  /**
   * A list of form fields
   * @type {import("../../molecules/FormFields/FormField.svelte").FormFieldProps[]}
   */
  export let formFields = [];

  /**
   * a list of field groups
   * @type {{label: string, formFields: import("../../molecules/FormFields/FormField.svelte").FormFieldProps[]}[]}
   */
  export let formFieldGroups = [];

  /**
   * Indicates whether the form is currently being submitted
   * @type {boolean}
   */
  export let isSubmitting = false;

  /**
   * The error message to display when the form submission fails
   * @type {string}
   */
  export let formSubmitErrorMessage = "";

  const dispatch = createEventDispatcher();

  /** Indicates that the input is valid after form is pre-submmited (submit clicked).
   * */
  let validityState = {};

  $: ariaLiveTextOnSubmitting = isSubmitting
    ? "sending"
    : formSubmitErrorMessage
      ? formSubmitErrorMessage
      : "";
  let ariaLiveTextOnValidatingFields;

  const submitButton = formFields.find((f) => f.type === "submit");

  /** The form submit event handler.
   * @param {SubmitEvent} event - The form submit event.
   */
  const onSubmit = (event) => {
    if (formProps.action && formProps.method) {
      return;
    }

    event.preventDefault();

    const hasInvalidFields = performManualValidation();
    if (hasInvalidFields) {
      console.error("form has invalid fields, submission halted.");
      return;
    }

    let formDataEntries = {};

    const formData = new FormData(event.target);
    for (const [key, val] of formData.entries()) {
      // group fields w/ multiple values
      if (formDataEntries[key]) {
        formDataEntries[key] = Array.isArray(formDataEntries[key])
          ? [...formDataEntries[key], val]
          : [formDataEntries[key], val];
      } else {
        formDataEntries[key] = val;
      }
    }

    dispatch("formSubmit", formDataEntries);
  };

  const handleInputValidity = (event) => {
    const { id } = event.detail;
    validityState = { ...validityState, [id]: false };
    ariaLiveTextOnValidatingFields += `${id} is not valid.`;
  };

  let formRef;
  const performManualValidation = () => {
    let hasInvalidFields = false;

    const formData = new FormData(formRef);
    for (const field of formFields) {
      if (field.required && !formData.get(field.name)) {
        handleInputValidity({ detail: { id: field.id } });

        field.isInvalid = true;
        // eslint-disable-next-line no-self-assign
        formFields = formFields;
        hasInvalidFields = true;
      } else {
        validityState = { ...validityState, [id]: true };
        field.isInvalid = false;
        // eslint-disable-next-line no-self-assign
        formFields = formFields;
      }
    }

    return hasInvalidFields;
  };

  const handleFormClick = () => {
    const hasInvalidFields = performManualValidation();

    if (!hasInvalidFields) {
      validityState = {};
      ariaLiveTextOnValidatingFields = ""; // Reset the aria live text
    }
  };
</script>

<form
  id="form-{id}"
  {...formProps}
  on:submit={onSubmit}
  class="flex flex-col gap-5"
  bind:this={formRef}
>
  <!-- fieldsets -->
  {#each formFieldGroups as group}
    <fieldset class="[&:not(:first-child)]:mt-10">
      <legend class="heading-sm mb-9">{group.label}</legend>
      {#each group.fields.filter((f) => f.type !== "submit") as formField (formField?.id)}
        <FormField
          {...formField}
          id={formField?.id || `${id}-${formField?.label}`}
          isValidValue={validityState[formField?.id]}
          on:invalid={handleInputValidity}
          componentClass={`mb-3 h-auto`}
        />
      {/each}
    </fieldset>
  {/each}

  <!-- individual fields -->
  {#each formFields.filter((f) => f.type !== "submit") as formField (formField?.id)}
    <FormField
      {...formField}
      id={formField?.id || `${id}-${formField?.label}`}
      isValidValue={validityState[formField?.id]}
      on:invalid={handleInputValidity}
      componentClass={`mb-3 h-auto`}
    />
  {/each}

  <!-- include a terms of service checkbox in every form -->
  <FormField
    id="termsOfService"
    name="termsOfService"
    type="checkbox"
    value="checked"
    required
    errorMessage="Please agree to the Terms of Service."
    isValidValue={validityState.termsOfService}
    on:invalid={handleInputValidity}
    componentClass={`mb-10 h-auto`}
  >
    <span>
      Yes, I agree to the
      <Link variant="inline" href="/terms-of-service" class="underline" label="Terms of Service" />
    </span>
  </FormField>

  <!-- reCAPTCHA goes here -->

  <!-- submit button -->
  <div class="relative">
    <FormField
      id={submitButton?.id || `${id}-submit`}
      type="submit"
      label={submitButton?.label || "Submit"}
      variant={isSubmitting || layout === "stacked" ? "filled" : "outline"}
      isLoading={isSubmitting}
      theme="white"
      buttonClass="mt-10 lg:mt-4"
      on:click={handleFormClick}
    />
    {#if formSubmitErrorMessage}
      <div class="label absolute pt-3 text-system-red">
        {formSubmitErrorMessage}
      </div>
    {/if}
  </div>

  <div aria-live="assertive" class="sr-only">
    {ariaLiveTextOnValidatingFields ? ariaLiveTextOnValidatingFields : ariaLiveTextOnSubmitting}
  </div>
</form>
